import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Loader2, Crown, TrendingUp, TrendingDown, Minus, Gift, Flame, Trophy } from 'lucide-react';
import { leaderboardAPI, mediaUrl } from '../services/api';

const Trend = ({ trend, delta }) => {
  if (trend === 'up') return <span className="flex items-center gap-0.5 text-green-500 text-xs font-bold" data-testid="trend-up"><TrendingUp className="w-3.5 h-3.5" />{delta}</span>;
  if (trend === 'down') return <span className="flex items-center gap-0.5 text-red-500 text-xs font-bold" data-testid="trend-down"><TrendingDown className="w-3.5 h-3.5" />{delta}</span>;
  return <span className="text-neutral-600 text-xs" data-testid="trend-stable"><Minus className="w-3.5 h-3.5" /></span>;
};

const PODIUM = {
  1: { h: 'h-28', ring: 'ring-orange-500', badge: 'bg-orange-500', label: '1', glow: 'shadow-[0_0_35px_rgba(255,94,0,0.55)]' },
  2: { h: 'h-20', ring: 'ring-neutral-300', badge: 'bg-neutral-300 text-neutral-900', label: '2', glow: '' },
  3: { h: 'h-16', ring: 'ring-amber-700', badge: 'bg-amber-700', label: '3', glow: '' },
};

const PodiumSpot = ({ e }) => {
  if (!e) return <div className="flex-1" />;
  const cfg = PODIUM[e.rank];
  return (
    <div className="flex-1 flex flex-col items-center justify-end" data-testid={`podium-${e.rank}`}>
      {e.rank === 1 && <Crown className="w-6 h-6 text-orange-500 mb-1" />}
      <div className="relative">
        <Avatar className={`${e.rank === 1 ? 'w-20 h-20' : 'w-14 h-14'} ring-4 ${cfg.ring} ${cfg.glow}`}>
          <AvatarImage src={mediaUrl(e.avatar)} />
          <AvatarFallback className="bg-neutral-800 text-white font-bold">{e.name?.charAt(0) || '?'}</AvatarFallback>
        </Avatar>
        <span className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full ${cfg.badge} text-white text-xs font-extrabold flex items-center justify-center ring-2 ring-[#070709]`}>{cfg.label}</span>
      </div>
      <p className="text-sm font-bold text-white mt-2 text-center truncate max-w-[90px]">{e.name}</p>
      <p className="text-orange-400 font-extrabold text-sm">{e.total_points}</p>
      <div className={`w-full ${cfg.h} mt-2 rounded-t-xl bg-gradient-to-t ${e.rank === 1 ? 'from-orange-600/40 to-orange-500/10 border-orange-500/40' : 'from-neutral-800 to-neutral-800/20 border-neutral-700'} border-t-2`} />
    </div>
  );
};

export default function Classement() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('general');
  const [boards, setBoards] = useState({ clients: null, distributeurs: null });

  useEffect(() => {
    leaderboardAPI.full().then(setData).catch(() => {});
    leaderboardAPI.clients().then((d) => setBoards((b) => ({ ...b, clients: d }))).catch(() => {});
    leaderboardAPI.distributeurs().then((d) => setBoards((b) => ({ ...b, distributeurs: d }))).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const { entries, me, next_tier } = data;
  const top3 = [entries[1], entries[0], entries[2]]; // 2 - 1 - 3
  const rest = entries.slice(3, 10);
  const showStickyBar = me.rank && !me.in_top10;

  const TABS = [
    { id: 'general', label: 'Général' },
    { id: 'clients', label: 'Clients' },
    { id: 'distributeurs', label: 'Distributeurs' },
  ];

  const routineList = tab === 'clients' ? boards.clients : boards.distributeurs;

  return (
    <div className="space-y-6 animate-rise pb-24">
      <div>
        <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white flex items-center gap-2">
          <Trophy className="w-8 h-8 text-orange-500" />Classement
        </h1>
        <p className="text-neutral-400 text-sm mt-1">Top 10 du challenge (Général) + classements Clients & Distributeurs par routines.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-neutral-900 rounded-full border border-neutral-800" data-testid="classement-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex-1 py-2 rounded-full text-sm font-bold transition-colors ${tab === t.id ? 'bg-orange-500 text-white' : 'text-neutral-400'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'general' ? (
        <>
          {/* Podium */}
          {entries.length > 0 && (
            <div className="flex items-end gap-2 px-2" data-testid="podium">
              <PodiumSpot e={top3[0]} />
              <PodiumSpot e={top3[1]} />
              <PodiumSpot e={top3[2]} />
            </div>
          )}

          {/* Next gift tier line */}
          {next_tier && (
            <Card className="p-4 rounded-2xl border-orange-500/30 bg-orange-500/10" data-testid="next-tier-line">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center shrink-0 overflow-hidden">
                  {next_tier.photo_url ? <img src={mediaUrl(next_tier.photo_url)} alt="" className="w-full h-full object-cover" /> : <Gift className="w-5 h-5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-orange-300 font-semibold uppercase tracking-wide">Prochain palier cadeau</p>
                  <p className="text-sm font-bold text-white truncate">{next_tier.name} · {next_tier.required_points} pts</p>
                </div>
                <span className="text-orange-400 font-extrabold text-sm shrink-0">-{next_tier.missing_points} pts</span>
              </div>
            </Card>
          )}

          {/* Ranks 4-10 */}
          <div className="space-y-2">
            {rest.map((e) => (
              <Card key={e.user_id} data-testid={`rank-row-${e.rank}`} className={`p-3 rounded-xl flex items-center gap-3 border ${e.is_me ? 'border-orange-500 bg-orange-500/10' : 'border-neutral-800 bg-neutral-900/60'}`}>
                <span className="w-6 text-center font-display font-extrabold text-lg text-neutral-400">{e.rank}</span>
                <Avatar className="w-10 h-10 ring-1 ring-neutral-700">
                  <AvatarImage src={mediaUrl(e.avatar)} />
                  <AvatarFallback className="bg-neutral-800 text-white text-xs">{e.name?.charAt(0) || '?'}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{e.name}{e.is_me && ' (toi)'}</p>
                  <p className="text-xs text-neutral-400 flex items-center gap-1"><Flame className="w-3 h-3 text-orange-500" />{e.current_streak} · {e.badges_count} badges</p>
                </div>
                <Trend trend={e.trend} delta={e.trend_delta} />
                <span className="text-orange-400 font-extrabold text-sm w-14 text-right">{e.total_points}</span>
              </Card>
            ))}
          </div>

          {/* Sticky bar for user outside Top 10 */}
          {showStickyBar && (
            <div className="fixed bottom-16 md:bottom-4 inset-x-0 z-30 px-4" data-testid="my-rank-sticky">
              <div className="max-w-5xl mx-auto rounded-2xl bg-gradient-to-r from-orange-600 to-red-600 shadow-[0_0_30px_rgba(255,94,0,0.4)] p-3.5 flex items-center gap-3">
                <span className="font-display font-extrabold text-2xl text-white">{me.rank}<sup className="text-sm">e</sup></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">Ta position actuelle</p>
                  <p className="text-xs text-white/90">
                    {me.gap_to_10th > 0
                      ? <>Il te manque <span className="font-extrabold">{me.gap_to_10th} pts</span> pour entrer dans le Top 10</>
                      : 'Rejoins le Top 10 en validant tes premiers défis 🔥'}
                  </p>
                </div>
                <Trophy className="w-6 h-6 text-white/80" />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-2" data-testid={`board-${tab}`}>
          <p className="text-xs text-neutral-400">{tab === 'clients' ? 'Classé sur les points des routines Client.' : 'Classé sur les points des routines Distributeur.'}</p>
          {!routineList ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
          ) : routineList.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-8">Aucun participant pour ce classement pour le moment.</p>
          ) : routineList.map((e) => (
            <Card key={e.user_id} data-testid={`routine-rank-${e.rank}`} className={`p-3 rounded-xl flex items-center gap-3 border ${e.is_me ? 'border-orange-500 bg-orange-500/10' : 'border-neutral-800 bg-neutral-900/60'}`}>
              <span className="w-6 text-center font-display font-extrabold text-lg text-neutral-400">{e.rank}</span>
              <Avatar className="w-10 h-10 ring-1 ring-neutral-700">
                <AvatarImage src={mediaUrl(e.avatar)} />
                <AvatarFallback className="bg-neutral-800 text-white text-xs">{e.name?.charAt(0) || '?'}</AvatarFallback>
              </Avatar>
              <p className="flex-1 min-w-0 text-sm font-bold text-white truncate">{e.name}{e.is_me && ' (toi)'}</p>
              <span className="text-orange-400 font-extrabold text-sm">{e.points} pts</span>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
